import {
  BasicButton,
  SectionMessage,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { RefObject } from "react";
import { Trans, useTranslation } from "react-i18next";

import { WhatsThis } from "../../components/WhatsThis";
import { SUCCESSFACTORS_HELP_URL } from "../webhookSample";

interface Props {
  isSubmitting: boolean;
  sapCertificateInputRef: RefObject<HTMLInputElement | null>;
  certificateUploadStatus: string | null;
  certificateParseError: string | null;
  showOutboundCertificateMaterial: boolean;
  needsManualCompanyId: boolean;
  displayCompanyId: string;
  x509MaterialPreview: string;
  samlMaterialPreview: string;
}

export function SuccessFactorsOutboundSection({
  isSubmitting,
  sapCertificateInputRef,
  certificateUploadStatus,
  certificateParseError,
  showOutboundCertificateMaterial,
  needsManualCompanyId,
  displayCompanyId,
  x509MaterialPreview,
  samlMaterialPreview,
}: Props) {
  const { t } = useTranslation();

  return (
    <>
      <Text
        style="global/heading-medium"
        testID="successfactors-outbound-heading"
      >
        {t("Outbound SAML Bearer")}
      </Text>
      <Text
        style="form/body-primary"
        testID="successfactors-outbound-description"
      >
        <Trans>
          To send background check status updates and results back to SAP
          SuccessFactors, Certn connects securely using{" "}
          <strong>
            OAuth 2.0 SAML Bearer authentication with an X.509 certificate
          </strong>
          .
        </Trans>
      </Text>
      <Text style="form/body-primary">
        {t(
          "Provide the details below to enable the connection between Certn and your SAP SuccessFactors account."
        )}
      </Text>
      <Text style="form/body-primary">
        <a
          href={SUCCESSFACTORS_HELP_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="successfactors-outbound-help-link"
        >
          {t("Learn how to set up the integration.")}
        </a>
      </Text>
      <TextInputFormikField
        name="success_factors_oauth_client_id"
        label={t("OAuth client ID")}
        testID="successfactors-oauth-client-id"
        isDisabled={isSubmitting}
        isRequired
        info={
          <WhatsThis>
            {t(
              "The OAuth client ID from the SuccessFactors OAuth application. There is no client secret for SAML Bearer."
            )}
          </WhatsThis>
        }
      />
      <TextInputFormikField
        name="api_user"
        label={t("API user")}
        testID="successfactors-api-user"
        isDisabled={isSubmitting}
        isRequired
        info={
          <WhatsThis>
            {t(
              "The SuccessFactors API user that owns the OAuth client and X.509 certificate."
            )}
          </WhatsThis>
        }
      />
      <TextInputFormikField
        name="success_factors_oauth_token_endpoint"
        label={t("Token URL")}
        testID="successfactors-oauth-token-endpoint"
        isDisabled={isSubmitting}
        isRequired
        info={
          <WhatsThis>
            {t(
              "The SuccessFactors OAuth token endpoint. Connect uses it to authenticate with SAP."
            )}
          </WhatsThis>
        }
      />
      <TextInputFormikField
        name="odata_base_url"
        label={t("OData base URL")}
        testID="successfactors-odata-base-url"
        isDisabled={isSubmitting}
      />
      <Stack gap="space-4" testID="successfactors-outbound-cert-upload">
        <Text style="form/body-secondary">
          {t(
            "Upload the certificate file downloaded from SAP Admin Center (Manage OAuth2 Client Applications)."
          )}
        </Text>
        <BasicButton
          type="button"
          testID="successfactors-sap-certificate-choose-file"
          isDisabled={isSubmitting}
          onPress={() => sapCertificateInputRef.current?.click()}
        >
          {t("Upload certificate")}
        </BasicButton>
        {certificateUploadStatus && (
          <Text
            style="form/body-primary"
            testID="successfactors-certificate-upload-status"
          >
            {certificateUploadStatus}
          </Text>
        )}
        {certificateParseError && (
          <SectionMessage
            type="danger"
            testID="successfactors-certificate-parse-error"
          >
            {certificateParseError}
          </SectionMessage>
        )}
      </Stack>
      {showOutboundCertificateMaterial && (
        <Stack gap="space-4" testID="successfactors-outbound-material">
          {needsManualCompanyId ? (
            <>
              <Text
                style="form/body-secondary"
                testID="successfactors-company-id-manual-hint"
              >
                {t(
                  "We could not read a Company ID from the SAP file. Enter your SAP SuccessFactors company ID, for example SFCPART000123."
                )}
              </Text>
              <TextInputFormikField
                name="company_id"
                label={t("Company ID")}
                testID="successfactors-company-id"
                isDisabled={isSubmitting}
                isRequired
              />
            </>
          ) : (
            <Stack gap="space-1">
              <Text style="form/body-secondary">{t("Company ID")}</Text>
              <Text
                style="form/body-primary"
                testID="successfactors-company-id"
              >
                {displayCompanyId}
              </Text>
            </Stack>
          )}
          <Stack gap="space-1">
            <Text style="form/body-secondary">{t("X.509 certificate")}</Text>
            <Text
              style="form/body-primary"
              testID="successfactors-x509-certificate-mask"
            >
              {x509MaterialPreview}
            </Text>
          </Stack>
          <Stack gap="space-1">
            <Text style="form/body-secondary">{t("Private key")}</Text>
            <Text
              style="form/body-primary"
              testID="successfactors-saml-private-key-mask"
            >
              {samlMaterialPreview}
            </Text>
          </Stack>
        </Stack>
      )}
    </>
  );
}
